import { ResponsiveLine, Serie } from "@nivo/line";
import ComponentLoadingBar from "../../utility/ComponentLoadingBar";

type LineChartProps = {
  // series.label is the name (e.g., "Count"); series.points = [{ x: "2025-08-12", y: 123 }, ...]
  series: { label: string; points: { x: string; y: number }[] };
  loading?: boolean;
  empty?: boolean;
};

// Build 7 zero-value days so the "no data" state still looks like a chart
function last7DaysZeroes(): { x: string; y: number }[] {
  const out: { x: string; y: number }[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const label = d.toISOString().slice(0, 10); // YYYY-MM-DD
    out.push({ x: label, y: 0 });
  }
  return out;
}

const LineChart = ({ series, empty }: LineChartProps) => {
  // Use real data if present; otherwise render a 7-day zero baseline so axes & layout remain intact
  const points = empty || !series?.points?.length ? last7DaysZeroes() : series.points;

  const data: Serie[] = [
    {
      id: series?.label ?? "Series",
      data: points.map((p) => ({ x: p.x, y: p.y })),
    },
  ];

  // if we have over 10 points, skip every other label to reduce clutter
  const tickValuesToShow = points.length > 10 ? points.filter((_, i) => i % 2 === 0).map((p) => p.x) : points.map((p) => p.x);

  return (
    <div className="h-full w-full">
      <ResponsiveLine
        data={data}
        // Minimal look
        theme={{
          text: { fontSize: 12, fill: "#374151" },
          axis: {
            ticks: { text: { fill: "#6B7280" } },
            legend: { text: { fill: "#6B7280" } },
          },
          grid: { line: { stroke: "#E5E7EB", strokeWidth: 1 } },
          crosshair: { line: { stroke: "#9CA3AF", strokeWidth: 1, strokeOpacity: 0.6 } },
          tooltip: { container: { fontSize: 12, borderRadius: 8 } },
        }}
        margin={{ top: 8, right: 12, bottom: 28, left: 40 }}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: 0, max: "auto", stacked: false, nice: true }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          tickRotation: 0,
          // Show MM-DD for neatness
          format: (v) => tickValuesToShow.find(vts => vts === v) ? v.slice(5) : "",
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 6,
          tickRotation: 0,
          format: (v) => (Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v)),
        }}
        enableGridX={false}
        enableGridY={true}
        curve="monotoneX"
        enablePoints={true}
        pointSize={0}
        enableSlices="x"
        useMesh={true}
        colors={["#22C55E"]} // soft green accent
        enableArea={true}
        areaOpacity={0.25}
        areaBaselineValue={0}
        defs={[
          {
            id: "areaGradient",
            type: "linearGradient",
            colors: [
              { offset: 0, color: "#22C55E", opacity: 0.25 },
              { offset: 100, color: "#22C55E", opacity: 0.05 },
            ],
          },
        ]}
        fill={[{ match: { id: series?.label ?? "Series" }, id: "areaGradient" }]}
        tooltip={({ point }) => (
          <div className="rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm">
            <div className="text-[11px] text-gray-600">{String(point.data.xFormatted || point.data.x)}</div>
            <div className="text-sm font-semibold tabular-nums">{point.data.yFormatted ?? point.data.y}</div>
          </div>
        )}
        animate={true}
        motionConfig="gentle"
      />
    </div>
  );
};

export default LineChart;
